import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components';
import { fetchWrapper } from '../../utils/apiHandlers';
import LoadingDotsComp from '../LoadingDotsComp';
import theme from '../../theme';

interface Prop {
  userid: string;
}

interface Message {
  from: 'bot' | 'user';
  text: string;
  path?: string;
  quote?: string;
}

const PATH_LABELS: { [key: string]: string } = {
  composed: 'Composed from a verified fact',
  verified: "Verified against the business's own text",
  'quote-fallback': "Quoted directly from the business's listing",
  'no-context': "Nothing in the site's content matched this",
  'no-answer': "The model didn't answer - fell back to their contact info",
  thanks: 'Canned reply', greeting: 'Canned reply', 'unknown-business': 'No account found for this userid',
}

const TestChatBotComp: React.FC<Prop> = ({ userid }) => {
  const [messages, setmessages] = useState<Message[]>([])
  const [question, setquestion] = useState('')
  const [waiting, setwaiting] = useState(false)
  const [selected, setselected] = useState<number | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, waiting])

  useEffect(() => {
    setmessages([])
    setselected(null)
  }, [userid])

  const ask = async () => {
    let q = question.trim()
    if (!q || waiting || !userid) return
    let history = messages.reduce((pairs: { q: string; a: string }[], m, i, arr) => {
      if (m.from === 'user' && arr[i + 1]?.from === 'bot') pairs.push({ q: m.text, a: arr[i + 1].text })
      return pairs
    }, []).slice(-3)

    setmessages((m) => [...m, { from: 'user', text: q }])
    setquestion('')
    setwaiting(true)
    try {
      let response: any = await fetchWrapper.post("/api/gettestanswer", { userid, question: q, history })
      setmessages((m) => {
        let next = [...m, { from: 'bot' as const, text: response.answer, path: response.path, quote: response.quote }]
        setselected(next.length - 1)
        return next
      })
    } catch (error) {
      setmessages((m) => [...m, { from: 'bot', text: 'Something unexpected happened, please try again later or contact support@miamigo.ai' }])
    }
    setwaiting(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') ask()
  }

  const selectedMsg = selected !== null ? messages[selected] : null

  return (
    <MainContainer>
      <Title>Test Chatbot</Title>
      <Layout>
        <ChatCol>
          <ChatWindow>
            {messages.length === 0 && <Placeholder>Ask a question the way a customer would. Follow-ups work too.</Placeholder>}
            {messages.map((m, i) => (
              <Bubble
                key={i} $fromUser={m.from === 'user'}
                $selected={m.from === 'bot' && selected === i}
                onClick={() => m.from === 'bot' && setselected(i)}
              >
                {m.text}
              </Bubble>
            ))}
            {waiting && <Bubble $fromUser={false}><LoadingDotsComp /></Bubble>}
            <div ref={bottomRef} />
          </ChatWindow>
          <InputRow>
            <Input placeholder="Ask a follow-up…" value={question} maxLength={250} onChange={(e) => setquestion(e.target.value)} onKeyDown={onKeyDown} />
            <SendButton onClick={ask} disabled={waiting || !question.trim()}>Send</SendButton>
          </InputRow>
        </ChatCol>

        <SidePanel>
          <PanelTitle>How this answer was found</PanelTitle>
          {selectedMsg ? (
            <>
              <PathBadge $ok={!!selectedMsg.path && selectedMsg.path !== 'no-context' && selectedMsg.path !== 'no-answer'}>
                {PATH_LABELS[selectedMsg.path || ''] || selectedMsg.path || 'Unknown'}
              </PathBadge>
              {selectedMsg.quote && (
                <>
                  <PanelLabel>SOURCE QUOTE</PanelLabel>
                  <Quote>&ldquo;{selectedMsg.quote.slice(0, 320)}&rdquo;</Quote>
                </>
              )}
            </>
          ) : (
            <Hint>Click a bot reply to see how it was answered.</Hint>
          )}
          <PanelFooter>
            <div>{messages.filter((m) => m.from === 'user').length} question{messages.filter((m) => m.from === 'user').length === 1 ? '' : 's'} this conversation</div>
          </PanelFooter>
        </SidePanel>
      </Layout>
    </MainContainer>
  );
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${theme.colors.text};
`

const Layout = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const ChatCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  height: 380px;
  overflow-y: auto;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
`

const Placeholder = styled.div`
  margin: auto;
  font-size: 13px;
  color: ${theme.colors.textMuted};
`

const Bubble = styled.div<{ $fromUser: boolean; $selected?: boolean }>`
  align-self: ${(p) => (p.$fromUser ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.$fromUser ? theme.colors.primary : theme.colors.surface)};
  color: ${(p) => (p.$fromUser ? '#fff' : theme.colors.text)};
  border: ${(p) => (p.$selected ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`)};
  padding: 10px 14px;
  border-radius: ${(p) => (p.$fromUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px')};
  max-width: 80%;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  cursor: ${(p) => (p.$fromUser ? 'default' : 'pointer')};
`

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`

const Input = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  font-size: 14px;
  outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`

const SendButton = styled.button`
  padding: 10px 20px;
  border-radius: ${theme.radius.md};
  border: none;
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: .5; cursor: default; }
`

const SidePanel = styled.div`
  width: 280px;
  flex-shrink: 0;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 380px;
  @media (max-width: 900px) {
    width: 100%;
  }
`

const PanelTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
`

const PathBadge = styled.div<{ $ok: boolean }>`
  background: ${(p) => (p.$ok ? '#E7F6ED' : theme.colors.background)};
  color: ${(p) => (p.$ok ? theme.colors.success : theme.colors.textMuted)};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
`

const PanelLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${theme.colors.textMuted};
  margin-top: 4px;
`

const Quote = styled.div`
  background: ${theme.colors.primarySoft};
  color: ${theme.colors.primaryDark};
  border-radius: 8px;
  padding: 10px;
  font-size: 11px;
  line-height: 1.6;
`

const Hint = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const PanelFooter = styled.div`
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid ${theme.colors.border};
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

export default TestChatBotComp
